import { useSupabaseClient } from "#imports";
import type { Tables } from "~~/types/database.types.ts";

export interface MapWithProfile extends Partial<Tables<"maps">> {
	profiles: Partial<Tables<"profiles">>;
}

export interface SpotBySlug extends Tables<"spots"> {
	map_slug: MapWithProfile;
	categories: Partial<Tables<"categories">>;
	cities: Partial<Tables<"cities">>;
}

export const useSpots = () => {
	const fetchRecentSpots = async (limit = 5) => {
		const { data, error } = await useSupabaseClient()
			.from("spots")
			.select(`
        *,
        map_slug:map_id!inner(
          current_slug
        ),
        categories:category_id (
          id,
          name
        ),
        cities:cities_id (
          id,
          name
        ),
        maps!inner (
          id,
          is_published,
          profiles!inner(
            id,
            display_name,
            mappr_handle,
            profile_image_url
          )
        ),
        citites:cities_id (
          id,
          name,
          country
        )
      `)
			.limit(limit)
			.eq("is_published", true)
			.eq("maps.is_published", true)
			.eq("maps.is_featured", true)
			.order("created_at", { ascending: false });

		if (error) throw new Error(error.message);
		return data;
	};

	const fetchSpotsByMapId = async (id: string) => {
		const { data, error } = await useSupabaseClient()
			.from("spots")
			.select(`
        *,
        map_slug:map_id!inner(
          current_slug
        ),
        categories:category_id (
          id,
          name
        )
      `)
			.eq("map_id", id)
			.eq("is_published", true)
			.order("created_at", { ascending: false });
		if (error) throw new Error(error.message);
		return data;
	};

	const fetchSpotById = async (id: string) => {
		const { data, error } = await useSupabaseClient()
			.from("spots")
			.select(`
        *,
        maps (*)
      `)
			.eq("id", id)
			.single();
		if (error) throw new Error(error.message);
		return data;
	};

	const fetchSpotBySlug = async (slug: string): Promise<SpotBySlug> => {
		const { data, error } = await useSupabaseClient()
			.rpc("get_spot_by_slug", { search_slug_param: slug })
			.select(`
        *,
        map_slug:map_id!inner(
          current_slug,
          title,
          id,
          cover_image_url,
          profiles:user_id (
            id,
            mappr_handle,
            profile_image_url,
            display_name,
            stripe_account_id,
            bio_text
          )
        ),
        categories:category_id (
          id,
          name,
          icon
        ),
        cities:cities_id (
          id,
          name,
          country
        )
      `)
			.single();
		if (error) throw new Error(error);
		return data;
	};

	return {
		fetchRecentSpots,
		fetchSpotById,
		fetchSpotBySlug,
		fetchSpotsByMapId,
	};
};
